import { Constants, TableInsertModel, TableRowModel, TableUpdateModel, ViewRowModel } from '@examdojo/supabase';

export type SourceType = (typeof Constants.question_bank.Enums.source_type)[number];

export type DocumentType = (typeof Constants.question_bank.Enums.document_type)[number];

export type SourceStatus = (typeof Constants.question_bank.Enums.source_status)[number];

export type SourceHttpModel = Pick<
  TableRowModel<'sources', 'question_bank'>,
  | 'id'
  | 'source_type'
  | 'document_type'
  | 'status'
  | 'raw_source'
  | 'tier'
  | 'metadata'
  | 'extracted_content'
  | 'processed_content'
  | 'parsing_instructions'
  | 'syllabus_id'
  | 'created_at'
  | 'created_by'
  | 'updated_at'
  | 'updated_by'
  | 'name'
  | 'source_ref'
>;

export type SourceStoreModel = Omit<SourceHttpModel, 'raw_source' | 'metadata'> & {
  raw_source: RawSourceItem[];
  processed_content: {
    markschemes: Array<{ page: number; text: string }>;
    questions: Array<{ page: number; text: string }>;
  } | null;
  metadata: {
    calculator_allowed: boolean;
  };
};

export interface RawSourceItem {
  key: string;
  type: 'questions' | 'markschemes';
  bucket: string;
}

export interface SourceUIModel extends SourceStoreModel {}

export type SourceCreateModel = Pick<
  TableInsertModel<'sources', 'question_bank'>,
  | 'source_type'
  | 'document_type'
  | 'status'
  | 'raw_source'
  | 'tier'
  | 'metadata'
  | 'extracted_content'
  | 'processed_content'
  | 'parsing_instructions'
  | 'syllabus_id'
  | 'name'
>;

export type SourceUpdateModel = Pick<
  TableUpdateModel<'sources', 'question_bank'>,
  | 'source_type'
  | 'document_type'
  | 'status'
  | 'raw_source'
  | 'tier'
  | 'metadata'
  | 'extracted_content'
  | 'processed_content'
  | 'parsing_instructions'
  | 'syllabus_id'
  | 'name'
  | 'source_ref'
>;

export type SourceListItemHttpModel = Pick<
  ViewRowModel<'admin_source_items_view'>,
  | 'id'
  | 'source_type'
  | 'document_type'
  | 'status'
  | 'created_at'
  | 'updated_at'
  | 'user_email'
  | 'program'
  | 'subject'
  | 'name'
  | 'tier'
  | 'syllabus_id'
  | 'ref_questions'
  | 'raw_source'
>;

export const SOURCE_TYPE_TO_LABEL: Record<SourceType, string> = {
  OFFICIAL_PAST_EXAM: 'Official Past Exam',
  TEXT_BOOK: 'Text Book',
  QUESTION_BANK: 'Question Bank',
  WEBSITE: 'Website',
};

export const DOCUMENT_TYPE_TO_LABEL: Record<DocumentType, string> = {
  PDF: 'PDF',
};

export const SOURCE_STATUS_LIST = Constants.question_bank.Enums.source_status;
export const SOURCE_TYPE_LIST = Constants.question_bank.Enums.source_type;
export const DOCUMENT_TYPE_LIST = Constants.question_bank.Enums.document_type;

export const SOURCE_STATUS_TO_LABEL: Record<SourceStatus, string> = {
  DRAFT: 'Draft',
  READY_FOR_OCR: 'Ready for OCR',
  OCR_IN_PROGRESS: 'OCR in Progress',
  OCR_COMPLETED: 'OCR Completed',
  OCR_FAILED: 'OCR Failed',
  PARSING_IN_PROGRESS: 'Parsing in Progress',
  PARSED: 'Parsed',
  DELETED: 'Deleted',
  READY_FOR_PARSING: 'Ready for Parsing',
  FAILED: 'Failed',
};

const SourceStatusClassNames: Record<SourceStatus, string> = {
  DRAFT: 'bg-neutral-100 text-neutral-600',
  READY_FOR_OCR: 'bg-teal-100 text-teal-600',
  OCR_IN_PROGRESS: 'bg-sky-100 text-sky-600',
  OCR_COMPLETED: 'bg-green-100 text-green-600',
  OCR_FAILED: 'bg-red-100 text-red-600',
  PARSING_IN_PROGRESS: 'bg-sky-100 text-sky-600',
  PARSED: 'bg-teal-100 text-teal-600',
  DELETED: 'bg-red-100 text-red-600',
  READY_FOR_PARSING: 'bg-amber-100 text-amber-600',
  FAILED: 'bg-red-100 text-red-600',
};

export function getSourceStatusClassName(status: SourceStatus | null): string {
  return status ? SourceStatusClassNames[status] : 'bg-yellow-300';
}
